import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, PackageOpen } from "lucide-react";
import { TASK_TYPES } from "@/lib/format";
import { formatHM, parseHM } from "@/lib/hours";
import { sanitizeHtml } from "@/lib/sanitize";

// Reorder an array item from srcIdx to dstIdx (immutable).
function reorderArray(arr, srcIdx, dstIdx) {
    if (srcIdx === dstIdx || srcIdx < 0 || dstIdx < 0) return arr;
    const copy = arr.slice();
    const [moved] = copy.splice(srcIdx, 1);
    copy.splice(dstIdx, 0, moved);
    return copy;
}

// Debounce helper
function useDebouncedSave(fn, delay = 600) {
    const [timer, setTimer] = useState(null);
    return (...args) => {
        if (timer) clearTimeout(timer);
        setTimer(setTimeout(() => fn(...args), delay));
    };
}

export default function SOPEdit() {
    const { id } = useParams();
    const nav = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    const load = useCallback(async () => {
        const { data } = await api.get(`/sop-templates/${id}`);
        setTpl(data);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const persist = useCallback(async (next) => {
        await api.patch(`/sop-templates/${id}`, {
            name: next.name, description: next.description, structure: next.structure,
        });
        setSavedAt(new Date());
    }, [id]);
    const savePatch = useDebouncedSave(persist, 700);

    const update = (updater) => {
        setTpl((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            savePatch(next);
            return next;
        });
    };

    if (!tpl) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

    return (
        <div className="p-6" data-testid="sop-edit-page">
            <div className="flex items-center gap-3 mb-4">
                <Link to="/sop" className="text-neutral-500 hover:text-neutral-800"><ArrowLeft size={16} /></Link>
                <div className="min-w-0 flex-1">
                    <div className="text-xs text-neutral-500">Template SOP {tpl.type === "project" ? "Progetto" : "Programma"}</div>
                    <input value={tpl.name} onChange={(e) => update({ ...tpl, name: e.target.value })}
                        data-testid="sop-edit-name"
                        className="font-display text-xl font-semibold tracking-tight text-neutral-900 bg-transparent border-b border-transparent hover:border-neutral-200 focus:border-neutral-400 focus:outline-none w-full" />
                </div>
                <div className="text-xs text-neutral-400">{savedAt ? `Salvato ${savedAt.toLocaleTimeString("it-IT")}` : "Modifica per salvare"}</div>
            </div>

            <div className="mb-4">
                <textarea rows={2} value={tpl.description || ""} onChange={(e) => update({ ...tpl, description: e.target.value })}
                    placeholder="Descrizione (facoltativa)"
                    className="w-full text-sm border border-neutral-200 rounded px-3 py-2 bg-white" />
            </div>

            {tpl.type === "project"
                ? <ProjectStructureEditor tpl={tpl} update={update} />
                : <ProgramStructureEditor tpl={tpl} update={update} />}
        </div>
    );
}

// ---------- Project structure editor ----------
function ProjectStructureEditor({ tpl, update }) {
    const structure = tpl.structure || { plan_text: "", programs: [] };
    const programs = structure.programs || [];
    const editorRef = useRef(null);
    const [floatBtn, setFloatBtn] = useState(null);

    // Initialize / re-sync editor HTML when the template loads (not on each keystroke).
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (structure.plan_text || "")) {
            editorRef.current.innerHTML = structure.plan_text
                ? sanitizeHtml(structure.plan_text)
                : "<p>Scrivi qui il piano strategico. Puoi selezionare una frase per creare un programma.</p>";
        }
        // eslint-disable-next-line
    }, [tpl.id]);

    const persistPlanText = () => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        update({ ...tpl, structure: { ...structure, plan_text: html } });
    };

    const onSelect = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) { setFloatBtn(null); return; }
        const range = sel.getRangeAt(0);
        if (!editorRef.current || !editorRef.current.contains(range.commonAncestorContainer)) { setFloatBtn(null); return; }
        const text = sel.toString().trim();
        if (text.length < 3) { setFloatBtn(null); return; }
        const r = range.getBoundingClientRect();
        setFloatBtn({ x: r.left + r.width / 2 + window.scrollX, y: r.top + window.scrollY - 6, text, range });
    };

    const createProgramFromSelection = () => {
        if (!floatBtn) return;
        const anchorId = crypto.randomUUID();
        const span = document.createElement("span");
        span.className = "plan-anchor";
        span.setAttribute("data-anchor-id", anchorId);
        span.textContent = floatBtn.text;
        floatBtn.range.deleteContents();
        floatBtn.range.insertNode(span);
        window.getSelection().removeAllRanges();
        const nextProg = {
            title: floatBtn.text.slice(0, 80),
            description: "",
            estimated_hours: 0,
            plan_anchor_id: anchorId,
            plan_anchor_text: floatBtn.text,
            tasks: [],
        };
        const newHtml = editorRef.current.innerHTML;
        update({ ...tpl, structure: { ...structure, plan_text: newHtml, programs: [...programs, nextProg] } });
        setFloatBtn(null);
    };

    const addProgram = () => {
        update({
            ...tpl,
            structure: {
                ...structure,
                programs: [...programs, { title: "Nuovo programma", description: "", estimated_hours: 0, tasks: [] }],
            },
        });
    };
    const updateProgram = (idx, next) => {
        const arr = programs.slice(); arr[idx] = next;
        update({ ...tpl, structure: { ...structure, programs: arr } });
    };
    const removeProgram = (idx) => {
        if (!window.confirm("Eliminare il programma dal template?")) return;
        const prog = programs[idx];
        // If this program is anchored to plan_text, unwrap that span so the plain
        // text stays in place but no longer highlighted.
        let newHtml = structure.plan_text || "";
        if (editorRef.current && prog?.plan_anchor_id) {
            const span = editorRef.current.querySelector(`.plan-anchor[data-anchor-id="${prog.plan_anchor_id}"]`);
            if (span) {
                const parent = span.parentNode;
                while (span.firstChild) parent.insertBefore(span.firstChild, span);
                parent.removeChild(span);
                parent.normalize();
                newHtml = editorRef.current.innerHTML;
            }
        }
        const arr = programs.filter((_, i) => i !== idx);
        update({ ...tpl, structure: { ...structure, plan_text: newHtml, programs: arr } });
    };

    return (
        <div className="space-y-4">
            <div className="bg-white border border-neutral-200 rounded-md p-4 relative" data-testid="sop-plan-editor-container">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Piano strategico (facoltativo)</div>
                    <div className="text-[11px] text-neutral-400">Seleziona una frase per creare un programma</div>
                </div>
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                    onMouseUp={onSelect} onKeyUp={onSelect} onInput={persistPlanText} onBlur={persistPlanText}
                    className="plan-editor rich-content text-neutral-800"
                    data-testid="sop-plan-editor" />
                {floatBtn && (
                    <button onClick={createProgramFromSelection}
                        data-testid="sop-create-program-btn"
                        className="plan-float-btn"
                        style={{ left: floatBtn.x, top: floatBtn.y, position: "fixed" }}>
                        + Crea programma
                    </button>
                )}
            </div>

            <div className="flex items-center justify-between">
                <div className="font-display text-sm font-semibold text-neutral-800">Programmi · {programs.length}</div>
                <button onClick={addProgram} data-testid="sop-add-program"
                    className="text-xs border border-neutral-300 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1">
                    <Plus size={12} /> Aggiungi programma
                </button>
            </div>

            {programs.length === 0 && (
                <div className="bg-white border border-dashed border-neutral-200 rounded-md p-6 text-center text-sm text-neutral-400">
                    Ancora nessun programma nel template.
                </div>
            )}
            {programs.map((p, idx) => (
                <ProgramEditor key={idx} program={p} idx={idx} onChange={(np) => updateProgram(idx, np)} onDelete={() => removeProgram(idx)} testIdBase={`program-${idx}`}
                    tplId={tpl.id}
                    onDropProgram={(srcIdx, dstIdx) => update((prev) => {
                        const arr = reorderArray(prev.structure.programs || [], srcIdx, dstIdx);
                        return { ...prev, structure: { ...prev.structure, programs: arr } };
                    })} />
            ))}
        </div>
    );
}

function ProgramStructureEditor({ tpl, update }) {
    const structure = tpl.structure || { title: "", description: "", tasks: [] };
    return (
        <ProgramEditor
            program={{ ...structure, estimated_hours: structure.estimated_hours || 0 }}
            onChange={(np) => update({ ...tpl, structure: { ...structure, ...np } })}
            testIdBase="program-root"
            allowDelete={false}
        />
    );
}

// ---------- Program editor (used inside both structures) ----------
function ProgramEditor({ program, idx, onChange, onDelete, testIdBase, allowDelete = true, tplId, onDropProgram }) {
    const [open, setOpen] = useState(true);
    const [dragOver, setDragOver] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const tasks = program.tasks || [];
    const setTasks = (arr) => onChange({ ...program, tasks: arr });

    const addTask = () => {
        setTasks([...tasks, { title: "Nuova task", task_type: "semplice", estimated_hours: 0, children: [] }]);
    };
    const updateTask = (i, next) => { const a = tasks.slice(); a[i] = next; setTasks(a); };
    const removeTask = (i) => setTasks(tasks.filter((_, j) => j !== i));
    const dropTask = (srcIdx, dstIdx) => setTasks(reorderArray(tasks, srcIdx, dstIdx));

    const extractAsSop = async () => {
        if (idx == null || !tplId) return;
        const name = window.prompt("Nome della nuova SOP di tipo programma:", `SOP · ${program.title || "programma"}`);
        if (!name) return;
        setExtracting(true);
        try {
            await api.post(`/sop-templates/${tplId}/extract-program`, { program_index: idx, name });
            window.alert("Estratto come nuovo SOP di tipo programma.");
        } catch (e) {
            window.alert((e?.response?.data?.detail) || e?.message || "Errore");
        } finally {
            setExtracting(false);
        }
    };

    const canDrag = onDropProgram && idx != null && !program.plan_anchor_id;
    return (
        <div className={`bg-white border ${dragOver ? "border-[#FFE500]" : "border-neutral-200"} rounded-md`}
            data-testid={testIdBase}
            draggable={canDrag}
            onDragStart={canDrag ? (e) => { e.dataTransfer.setData("sop-program-idx", String(idx)); e.dataTransfer.effectAllowed = "move"; } : undefined}
            onDragOver={canDrag ? (e) => { if (e.dataTransfer.types.includes("sop-program-idx")) { e.preventDefault(); setDragOver(true); } } : undefined}
            onDragLeave={canDrag ? () => setDragOver(false) : undefined}
            onDrop={canDrag ? (e) => {
                const srcIdx = Number(e.dataTransfer.getData("sop-program-idx"));
                setDragOver(false);
                if (!Number.isNaN(srcIdx)) onDropProgram(srcIdx, idx);
            } : undefined}>
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
                {canDrag && <span className="text-neutral-300 cursor-grab select-none" title="Trascina per riordinare">⋮⋮</span>}
                <button onClick={() => setOpen(!open)} className="text-neutral-500">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button>
                <input value={program.title || ""} onChange={(e) => onChange({ ...program, title: e.target.value })}
                    className="text-sm font-medium text-neutral-900 bg-transparent border-b border-transparent hover:border-neutral-200 focus:border-neutral-400 focus:outline-none flex-1 min-w-0"
                    placeholder="Titolo programma"
                    data-testid={`${testIdBase}-title`} />
                {tplId && idx != null && (
                    <button onClick={extractAsSop} disabled={extracting} data-testid={`${testIdBase}-extract`}
                        className="text-xs border border-neutral-200 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1 text-neutral-700 disabled:opacity-60"
                        title="Salva questo programma come SOP separata">
                        <PackageOpen size={12} /> {extracting ? "Estraggo…" : "Estrai come SOP"}
                    </button>
                )}
                {allowDelete && (
                    <button onClick={onDelete} data-testid={`${testIdBase}-delete`} className="text-neutral-400 hover:text-red-600"><Trash2 size={13} /></button>
                )}
            </div>
            {open && (
                <div className="p-3 space-y-3">
                    <input value={program.description || ""} onChange={(e) => onChange({ ...program, description: e.target.value })}
                        placeholder="Descrizione (facoltativa)"
                        className="w-full text-xs border border-neutral-200 rounded px-2 py-1" />
                    <div className="space-y-1">
                        {tasks.length === 0 && <div className="text-xs text-neutral-400 px-1">Nessuna task nel template.</div>}
                        {tasks.map((t, i) => (
                            <TaskEditor key={i} task={t} idx={i} onDropTask={dropTask}
                                onChange={(nt) => updateTask(i, nt)} onDelete={() => removeTask(i)} testIdBase={`${testIdBase}-task-${i}`} />
                        ))}
                    </div>
                    <button onClick={addTask} data-testid={`${testIdBase}-add-task`}
                        className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                        <Plus size={12} /> Aggiungi task
                    </button>
                </div>
            )}
        </div>
    );
}

// ---------- Task editor (recursively supports one level of children) ----------
function TaskEditor({ task, idx, onDropTask, onChange, onDelete, testIdBase }) {
    const [open, setOpen] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [hoursStr, setHoursStr] = useState(formatHM(task.estimated_hours));
    const children = task.children || [];
    const hasChildren = children.length > 0;
    const derivedHours = hasChildren
        ? children.reduce((sum, c) => sum + (Number(c.estimated_hours) || 0), 0)
        : null;
    useEffect(() => {
        setHoursStr(formatHM(hasChildren ? derivedHours : task.estimated_hours));
    }, [task.estimated_hours, hasChildren, derivedHours]);

    const setChildren = (arr) => onChange({ ...task, children: arr });
    const addChild = () => setChildren([...children, { title: "Nuova task secondaria", task_type: "semplice", estimated_hours: 0 }]);
    const updateChild = (i, next) => { const a = children.slice(); a[i] = next; setChildren(a); };
    const removeChild = (i) => setChildren(children.filter((_, j) => j !== i));
    const dropChild = (srcIdx, dstIdx) => setChildren(reorderArray(children, srcIdx, dstIdx));

    const commitHours = () => {
        if (hasChildren) return;
        const v = parseHM(hoursStr);
        onChange({ ...task, estimated_hours: v == null ? 0 : v });
    };

    const canDrag = onDropTask && idx != null;
    return (
        <div className={`rounded border ${dragOver ? "border-[#FFE500] bg-emerald-50/40" : "border-neutral-100"}`}
            data-testid={testIdBase}
            draggable={canDrag}
            onDragStart={canDrag ? (e) => { e.dataTransfer.setData("sop-task-idx", String(idx)); e.dataTransfer.effectAllowed = "move"; e.stopPropagation(); } : undefined}
            onDragOver={canDrag ? (e) => { if (e.dataTransfer.types.includes("sop-task-idx")) { e.preventDefault(); setDragOver(true); } } : undefined}
            onDragLeave={canDrag ? () => setDragOver(false) : undefined}
            onDrop={canDrag ? (e) => {
                const src = Number(e.dataTransfer.getData("sop-task-idx"));
                setDragOver(false);
                e.stopPropagation();
                if (!Number.isNaN(src)) onDropTask(src, idx);
            } : undefined}>
            <div className="flex items-center gap-2 px-2 py-1.5">
                {canDrag && <span className="text-neutral-300 cursor-grab select-none" title="Trascina per riordinare">⋮⋮</span>}
                <button onClick={() => setOpen(!open)} className="text-neutral-400" title="Task secondarie">
                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
                <input value={task.title || ""} onChange={(e) => onChange({ ...task, title: e.target.value })}
                    placeholder="Titolo task"
                    data-testid={`${testIdBase}-title`}
                    className="text-sm flex-1 min-w-0 border border-transparent hover:border-neutral-200 focus:border-neutral-400 focus:outline-none px-1 py-0.5 rounded" />
                {!hasChildren && (
                    <select value={task.task_type || "semplice"} onChange={(e) => onChange({ ...task, task_type: e.target.value })}
                        data-testid={`${testIdBase}-type`}
                        className="text-xs border border-neutral-200 rounded px-1 py-0.5">
                        {TASK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                )}
                <input value={hoursStr} onChange={(e) => setHoursStr(e.target.value)} onBlur={commitHours}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    readOnly={hasChildren}
                    placeholder="0:00"
                    title={hasChildren ? "Somma automatica delle task secondarie" : "Ore stimate (H:MM)"}
                    className={`w-16 text-xs border rounded px-1 py-0.5 text-right ${hasChildren ? "bg-neutral-100 text-neutral-500 border-neutral-200 cursor-not-allowed" : "border-neutral-200"}`}
                    data-testid={`${testIdBase}-hours`} />
                <button onClick={onDelete} data-testid={`${testIdBase}-delete`} className="text-neutral-400 hover:text-red-600"><Trash2 size={12} /></button>
            </div>
            {open && (
                <div className="border-t border-neutral-100 px-2 py-2 space-y-1 bg-neutral-50/40">
                    {children.length === 0 && <div className="text-[11px] text-neutral-400 px-1">Nessuna task secondaria.</div>}
                    {children.map((c, i) => (
                        <ChildTaskEditor key={i} task={c} idx={i} onDropChild={dropChild}
                            onChange={(nc) => updateChild(i, nc)} onDelete={() => removeChild(i)} testIdBase={`${testIdBase}-child-${i}`} />
                    ))}
                    <button onClick={addChild} data-testid={`${testIdBase}-add-child`}
                        className="text-[11px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 px-1">
                        <Plus size={11} /> Aggiungi task secondaria
                    </button>
                </div>
            )}
        </div>
    );
}

function ChildTaskEditor({ task, idx, onDropChild, onChange, onDelete, testIdBase }) {
    const [hoursStr, setHoursStr] = useState(formatHM(task.estimated_hours));
    const [dragOver, setDragOver] = useState(false);
    useEffect(() => setHoursStr(formatHM(task.estimated_hours)), [task.estimated_hours]);
    const commitHours = () => {
        const v = parseHM(hoursStr);
        onChange({ ...task, estimated_hours: v == null ? 0 : v });
    };
    const canDrag = onDropChild && idx != null;
    return (
        <div className={`rounded border ${dragOver ? "border-[#FFE500] bg-emerald-50/40" : "border-neutral-100"} bg-neutral-50/60`}
            data-testid={testIdBase}
            draggable={canDrag}
            onDragStart={canDrag ? (e) => { e.dataTransfer.setData("sop-child-idx", String(idx)); e.dataTransfer.effectAllowed = "move"; e.stopPropagation(); } : undefined}
            onDragOver={canDrag ? (e) => { if (e.dataTransfer.types.includes("sop-child-idx")) { e.preventDefault(); setDragOver(true); } } : undefined}
            onDragLeave={canDrag ? () => setDragOver(false) : undefined}
            onDrop={canDrag ? (e) => {
                const src = Number(e.dataTransfer.getData("sop-child-idx"));
                setDragOver(false);
                e.stopPropagation();
                if (!Number.isNaN(src)) onDropChild(src, idx);
            } : undefined}>
            <div className="flex items-center gap-2 px-2 py-1.5">
                {canDrag && <span className="text-neutral-300 cursor-grab select-none" title="Trascina per riordinare">⋮⋮</span>}
                <input value={task.title || ""} onChange={(e) => onChange({ ...task, title: e.target.value })}
                    placeholder="Titolo task secondaria"
                    data-testid={`${testIdBase}-title`}
                    className="text-sm flex-1 min-w-0 border border-transparent hover:border-neutral-200 focus:border-neutral-400 focus:outline-none px-1 py-0.5 rounded" />
                <select value={task.task_type || "semplice"} onChange={(e) => onChange({ ...task, task_type: e.target.value })}
                    data-testid={`${testIdBase}-type`}
                    className="text-xs border border-neutral-200 rounded px-1 py-0.5">
                    {TASK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <input value={hoursStr} onChange={(e) => setHoursStr(e.target.value)} onBlur={commitHours}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    placeholder="0:00"
                    title="Ore stimate (H:MM)"
                    className="w-16 text-xs border border-neutral-200 rounded px-1 py-0.5 text-right" />
                <button onClick={onDelete} data-testid={`${testIdBase}-delete`} className="text-neutral-400 hover:text-red-600"><Trash2 size={12} /></button>
            </div>
        </div>
    );
}
